import React, { useEffect, useState } from "react";
import SummaryTable, {
  SummaryTableRow,
} from "@appComponents/SummaryTable/SummaryTable";
import IDepositFiatAccount, {
  IPartnerBankAccount,
} from "./IDepositFiatAccount";
import FundsService from "@services/Funds.service";
import ToolTip from "@appComponents/ToolTip/ToolTip";
import InfoBox from "@appComponents/InfoBox/InfoBox";
import BankForm from "../BankForm/BankForm";
import * as style from "./DepositFiatAccountStyle.module.scss";
import { catchError, displayBankNumber } from "@features/utils/functions";
import { FundsAppContext } from "@features/context";
import { withRouter } from "react-router";
import { unauthorizedPushNotification } from "@features/utils/reactFunctions";
import { TReducers } from "@features/reducers";
import { connect } from "react-redux";
import ApiDefined from "@appInterfaces/general.types";
import ActionBox from "@appComponents/ActionBox/ActionBox";
import Flag from "@appComponents/Flag/Flag";
import { WarningBox } from "../WarningBox/WarningBox";
import {
  fdtDepositRestrictedCountries,
  fdtConsiderStableCoins,
} from "../WarningBox/warningMessages";
import { getCountryNamesArray } from "@funds/features/helpers/functions";

import * as bankAccountBoxStyle from "../BankAccounts/components/BankAccountBox/BankAccountBoxStyle.module.scss";

const labels: { [key: string]: string } = {
  account_number: "Account Number",
  bank_name: "Bank Name",
  bank_address: "Bank Address",
  beneficiary_name: "Beneficiary Name",
  beneficiary_address: "Beneficiary Address",
  bic_swift: "BIC/SWIFT",
  reference_number: "Reference Number",
};

const DepositFiatAccount: React.FunctionComponent<IDepositFiatAccount> = (
  props
) => {
  const { appLink } = React.useContext(FundsAppContext);
  const [partnerDepositBankAccount, setPartnerDepositBankAccount] =
    useState<ApiDefined<IPartnerBankAccount>>(undefined);

  const blockedCountries = getCountryNamesArray(props.fiatRestrictedCountries);

  useEffect(() => {
    if (props.bankAccounts.find((a) => a.status === "active")) {
      FundsService.getDepositBankAccount()
        .then((res) => {
          setPartnerDepositBankAccount(res.data);
        })
        .catch((err) => {
          if (err.response.status === 424) {
            unauthorizedPushNotification(
              unauthorizedRedirect,
              `We could not perform this action because the requested action
            depended on external service and that service failed. Please try again later.`
            );
            return;
          }
          catchError(err, appLink);
        });
    }
  }, [props.bankAccounts]);

  const unauthorizedRedirect = () => props.history.replace(appLink);

  const renderTableContent = () => {
    return partnerDepositBankAccount
      ? Object.keys(partnerDepositBankAccount).map((el, i) => {
          const skipped = ["id", "investor_id", "current_bank_account_id"];
          if (skipped.includes(el)) return;

          const label = labels[el];
          const value = partnerDepositBankAccount[el];

          return (
            value && (
              <SummaryTableRow
                key={i}
                label={label}
                value={formatValue(label, value)}
              />
            )
          );
        })
      : null;
  };

  const formatValue = (
    label: string,
    value: string | string[]
  ): string | JSX.Element => {
    switch (label) {
      case "Currency":
        return `${String(value).toUpperCase()} ${
          props.selectedCoin !== "usd"
            ? `(converted from ${props.selectedCoin!.toUpperCase()})`
            : ""
        }`;
      case "Bank Address":
        return Array.isArray(value) ? value.join(", ") : value;
      case "Short Reference":
      case "Reference Number":
        return (
          <div className={style.tooltipText}>
            <ToolTip
              text="Please use this reference as your transaction description.
              Please ensure the reference number is correct and refrain from adding any additional details
              to ensure the transfer doesn't get delayed."
              maxWidth={250}
            >
              <strong className={style.border}>{value}</strong>
            </ToolTip>
          </div>
        );
      default:
        return String(value);
    }
  };

  const accounts = props.bankAccounts.map((el, i) => {
    const isRequested = el.status === "email_sent" || el.status === "pending";

    return (
      <ActionBox
        type="empty"
        isRequested={isRequested}
        key={i}
        headline={
          <>
            <p>{el.name}</p>
            &nbsp;
            <Flag slug={el.country} />
          </>
        }
        text={
          <>
            <p className={style.accountNumber}>
              {displayBankNumber(el.iban || el.account_number)}
            </p>
            {el.status === "pending" ? (
              <p className={bankAccountBoxStyle.requestedLabel}>
                This account is currently being reviewed. Once approved you will
                be able to use this account for deposits. Note: you can only
                have one active account on your profile.
              </p>
            ) : el.status === "email_sent" ? (
              <p className={bankAccountBoxStyle.requestedLabel}>
                This account cannot be used for deposit purpose yet. It must be
                confirmed first, check your inbox.
              </p>
            ) : (
              <p className={bankAccountBoxStyle.fromThisLabel}>
                You have to deposit from this particular bank account.
              </p>
            )}
          </>
        }
      />
    );
  });

  const displayContent = () => {
    if (accounts.length > 0) {
      return (
        <>
          <h3 className={style.headlineAccount}>Bank Accounts</h3>
          {accounts}
          {partnerDepositBankAccount && (
            <>
              <hr className={style.hr} />
              <WarningBox
                texts={[
                  fdtDepositRestrictedCountries(blockedCountries),
                  fdtConsiderStableCoins,
                ]}
              />

              <WarningBox
                texts={[
                  <>
                    Please don't forget to include the{" "}
                    <b>CMR reference number</b> below to your transfer.
                    <br />A new code is generated here for each Deposit request.
                    A transfer without the correct reference code will result in
                    extra delays and may be returned back to your bank.
                  </>,
                ]}
              />
              <p className={style.depositFromInfo}>Deposit into:</p>
              <SummaryTable>{renderTableContent()}</SummaryTable>
            </>
          )}
        </>
      );
    }

    return (
      <>
        <InfoBox>
          <p>
            To proceed with deposit please register your bank account. Note we
            are only able to process deposits from an account in your name.
          </p>
        </InfoBox>
        <BankForm values={props.values} errors={props.errors} />
        <WarningBox
          texts={[
            fdtDepositRestrictedCountries(blockedCountries),
            fdtConsiderStableCoins,
          ]}
        />
      </>
    );
  };

  return displayContent();
};

const mapStateToProps = (store: TReducers) => ({
  bankAccounts: store.fundsReducer.bankAccounts || [],
  fiatRestrictedCountries: store.appSettings.fiat_restricted_countries,
});

export default withRouter<
  Omit<IDepositFiatAccount, "bankAccounts" | "fiatRestrictedCountries">
>(connect(mapStateToProps)(DepositFiatAccount));
